import type * as React from 'react';

import { Navigate, Route, Routes as RouterRoutes } from 'react-router-dom';

import { AppLayout } from '@/components/AppLayout';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { Roles } from '@/constants/roles';
import { Routes } from '@/constants/routes';
import { CallbackPage } from '@/pages/CallbackPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { OrdersPage } from '@/pages/orders/OrdersPage';
import { CalendarPage } from '@/pages/orders/calendar/CalendarPage';
import { PlacesPage } from '@/pages/orders/places/PlacesPage';
import { AdminUsersPage } from '@/pages/admin/AdminUsersPage';
import { AdminNotificationsPage } from '@/pages/admin/AdminNotificationsPage';
import { useAppSelector } from '@/store/hooks';
import { selectUserRoles } from '@/store/userSlice';

// Home → first allowed route. Admin-only users (no backoffice/dispatcher)
// land on /admin/users; everyone else lands on /orders. This avoids the
// admin user immediately bouncing into ForbiddenPage on first load.
const HomeRedirect = (): React.JSX.Element => {
  const roles = useAppSelector(selectUserRoles);
  // `viewer` is an operational-overview reader (lands on /orders), not an
  // admin (CHR-03 §2 / ADR-0046).
  const hasOperational =
    roles.includes(Roles.BACKOFFICE) ||
    roles.includes(Roles.DISPATCHER) ||
    roles.includes(Roles.VIEWER);
  if (!hasOperational && roles.includes(Roles.ADMIN)) {
    return <Navigate to={Routes.ADMIN_USERS} replace />;
  }
  return <Navigate to={Routes.ORDERS} replace />;
};

export const AppRouter = (): React.JSX.Element => (
  <RouterRoutes>
    {/* OIDC redirect target — rendered before AuthGate has a chance to
        finalise auth state. Lives outside AppLayout (no chrome). */}
    <Route path={Routes.CALLBACK} element={<CallbackPage />} />

    {/* Everything else lives under the chrome layout. */}
    <Route element={<AppLayout />}>
      <Route path={Routes.HOME} element={<HomeRedirect />} />
      <Route
        path={Routes.ORDERS}
        element={
          <ProtectedRoute roles={[Roles.BACKOFFICE, Roles.DISPATCHER, Roles.VIEWER]}>
            <OrdersPage />
          </ProtectedRoute>
        }
      />
      {/* Weekly transport calendar (CHR-03 §6 / ADR-0051) — same chrome +
          role set as the overview; read-only for every role. */}
      <Route
        path={Routes.ORDERS_CALENDAR}
        element={
          <ProtectedRoute roles={[Roles.BACKOFFICE, Roles.DISPATCHER, Roles.VIEWER]}>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      {/* Adresář — the shared place registry (CHR-06 §1 / ID-236). Same
          chrome + role set as the overview; writes are role-gated inside the
          page (viewer is read-only) and enforced by the api. */}
      <Route
        path={Routes.ORDERS_ADDRESSES}
        element={
          <ProtectedRoute roles={[Roles.BACKOFFICE, Roles.DISPATCHER, Roles.VIEWER]}>
            <PlacesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={Routes.ADMIN_USERS}
        element={
          <ProtectedRoute roles={[Roles.ADMIN]}>
            <AdminUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={Routes.ADMIN_NOTIFICATIONS}
        element={
          <ProtectedRoute roles={[Roles.ADMIN]}>
            <AdminNotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFoundPage />} />
    </Route>
  </RouterRoutes>
);
